import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import moment from 'moment';
import TagInput from '../TagInput';
import MarkupEditor from '../../../../common/components/MarkupEditor';
import { useFormValidation } from 'common/hooks/useFormValidation';

import 'moment/locale/fi';
import { useLoadingIndication } from 'common/hooks/useLoadingIndication';
import LoadingIndicator from 'common/components/LoadingIndicator';
import InputFeedback from 'components/common/form/InputFeedback';
import LogoInput from 'components/LogoInput/LogoInput';
import Expression from './rule/Expression';
import { getRuleDefinition } from './rule/RuleService';
import { useAuthentication } from 'common/contexts/Authentication';
import { METHOD_NOT_ALLOWED } from 'http-status-codes';


const logoValidationRules = window.voucherManagementConfig.api.logoValidationCriteria;
const stampsUseTypeSettings = window.voucherManagementConfig.api.stampsUseTypeSettings;

const Form = ({
  model = {},
  tagSuqggestions = [],
  dateFormat = 'DD.MM.YYYY',
  timeFormat = 'HH:mm:ss',
  dateTimeFormat = 'DD.MM.YYYY HH:mm:ss',
  discountTypes = [],
  voucherUseTypes = [],
  onChange = (name, val) => { },
  onSubmit = () => { },
  t,
  locale = 'fi',
  children
}) => {
  const [useCountLimited, setUseCountLimited] = useState(model?.validity?.maxUseCount ?? 1);
  const [tags, setTags] = useState(model?.tags?.map(tag => ({ id: tag, text: tag })) ?? []);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [validationErrors, validate] = useFormValidation({}, t);
  const [ width, setWidth ] = useState(0);
  const [ first, setFirst ] = useState(true);
  const [startLoading, finishLoading, renderIndicatorIfLoading] = useLoadingIndication(<LoadingIndicator />);
  const formRef = useRef();
  const widthRef = useRef();
  const authentication = useAuthentication();
  const [ stampRuleDefinition, setStampRuleDefinition ] = useState([]);
  const [ stampRuleCheck, setStampRuleCheck ] = useState(true);
  const [ useCountEnabled, setUseCountEnabled ] = useState(false);

  const handleChange = (e, propName, propValue) => {
    const { name, value } = propName ? { name: propName, value: propValue } : e.target;
    onChange(name, value);
  };

  const handleTagChange = (op, value) => {
    if (op === 'add') {
      const existsInSuggestions = tag => tag.id === value;
      if (!tagSuqggestions.some(existsInSuggestions)) return;

      setTags([...tags, { id: value, text: value }]);
    } else if (op === 'remove') {
      setTags(tags.filter((tag, index) => value !== index));
    }
  };

  const handleLogoChange = ({ logo, filename }) => {
    onChange(undefined, { logo: logo, logoFilename: filename });
  };

  const handleDateChange = (dateType, val) => {
    let date = val;
    if(dateType === 'validUntil' && (date && date.toISOString().split('T')[1] === '00:00:00.000Z')){
      date = `${date.toISOString().split('T')[0]}T23:59:59.000Z`;
    }
    onChange('validity', { [dateType]: date ?? date.toISOString() });
  };

  const handleRuleChange = (e, propName, propValue) => {
    const { name, value } = propName ? { name: propName, value: propValue } : e.target;

    if (name.startsWith('expression')) {
      const [, expressionKey, , parameterKey] = name.split('_');

      const rule = model.rule || stampRuleDefinition;
      if(!rule.parameterValues) rule.parameterValues = {};
      if (!rule.parameterValues[expressionKey]) {
        rule.parameterValues[expressionKey] = {};
      }

      rule.parameterValues[expressionKey][parameterKey] = value;

      onChange('rule', rule);
      return;
    }

    onChange(name, value);
  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!validate(e.target)) return;

    if (formSubmitted) return;

    setFormSubmitted(true);
  };

  const handleDisable = (e) => {
    if(!hasUseCountSet && e.target.name === 'maxUseCount'){
      setUseCountEnabled(false);
    }
    else{
      setUseCountEnabled(true);
    }
  };

  useEffect(() => {
    onChange('tags', tags.map(t => t.id));
  }, [tags]);

  useEffect(() => {
    if(widthRef.current && first){
      setFirst(false);
      setWidth(widthRef.current.offsetWidth);
    }
  }, [ widthRef ])


  useEffect(() => {
    if (formSubmitted) {
      startLoading();
      onSubmit().then(() => {
        setFormSubmitted(false);
        finishLoading();
      });
    }
  }, [formSubmitted]);

  useEffect(() => {
    const doesNotHaveRule = !model.rule || !model.rule.key;
    const fillRule = async () => {
      if (model.useType === 'STAMPS' && doesNotHaveRule) {
        const rule = await getRuleDefinition(authentication.getMerchantUuid(), stampsUseTypeSettings.businessRuleKey);
        setStampRuleDefinition(rule);
      }
      else{
        Promise.resolve();
      }
    };
    fillRule().then(() => setStampRuleCheck(false));
  }, [model.useType]);

  let hasUseCountSet = model.validity.maxUseCount > 0;
  const stampsUseTypeDisabled = (option) => option.value === 'STAMPS' && !stampRuleDefinition;
  const isStampsCollectionAllowed = () => authentication.isSystemAdmin();

  return (
    <form noValidate ref={formRef} onSubmit={handleSubmit} className="yoso-form" style={ width > 0 ? {width} : null}>
      <div className="mb-3">
        <label htmlFor="name" className="form-label">{t('voucher.name')}</label>
        <input ref={widthRef} type="text" id="name" name="name" className="form-control" value={model.name ? model.name.fi : ''} onChange={(e) => handleChange(e, 'name', { fi: e.target.value })} required minLength={2} maxLength={100} autoFocus />
        <InputFeedback>
          {validationErrors.name}
        </InputFeedback>
      </div>

      <div className="mb-3">
        <label htmlFor="description" className="form-label">{t('voucher.description')}</label>
        <input type="text" name="description" value={model?.description.fi ?? ''} required style={{ display: 'none' }} minLength={2} maxLength={500} />
        <MarkupEditor id="description" name="description" markup={model.description ? model.description.fi : ''} onChange={(val) => handleChange(null, 'description', { fi: val })} maxLength={500} />
        <InputFeedback>
          {validationErrors.description}
        </InputFeedback>
      </div>
      <div className="mb-3 form-switch form-check">
        <input
          className="form-check-input"
          type="checkbox"
          value={-1}
          id="publiclyAvailable"
          onChange={(e) => handleChange(e, 'publiclyAvailable', e.target.checked)}
          checked={model.publiclyAvailable}
        />
        <label className="form-check-label align-middle" htmlFor="publiclyAvailable">
          {t('form.labels.publiclyAvailable')}
        </label>
      </div>

      <div className="mb-3">
        <label htmlFor="tags" className="form-label">{t('voucher.tags')}</label>
        <TagInput name="tags" placeholder={t('form.placeholders.tags')} tags={tags} suggestions={tagSuqggestions} onTagAdded={(tag) => handleTagChange('add', tag.id)} onTagDeleted={index => handleTagChange('remove', index)} />
        <InputFeedback>
          {validationErrors.tags}
        </InputFeedback>
      </div>

      <div className="row mb-3">
        <Discount name="discount" discountTypes={discountTypes} model={model.discount} onChange={handleChange} t={t} />
      </div>


      <div className="mb-3">
        <label htmlFor="name" className="col-3 col-form-label">{t('voucher.useType')}</label>
        <select className="form-select" onChange={handleChange} name="useType" value={model.useType} required>
          {voucherUseTypes.map(option => {
            if ((option.value === 'STAMPS' && isStampsCollectionAllowed()) || option.value !== 'STAMPS') {
              return <option key={option.value} value={option.value} disabled={stampsUseTypeDisabled(option)}>{option.label}</option> 
            } else {
              return '';
            }
          })
          }
        </select>
      </div>

      {model.useType === 'SLIDER' || model.useType === 'QR_CODE' ? <>
        <div className="mb-3 form-switch form-check">
          <input
            className="form-check-input"
            type="checkbox"
            value={-1}
            id="continuous"
            onChange={(e) => {
              e.target.checked ? setUseCountEnabled(true) : setUseCountEnabled(false);
              handleChange(e, 'validity', { maxUseCount: e.target.checked ? 1 : -1 })
            }}
            checked={useCountEnabled}
          />
          <label className="form-check-label align-middle" htmlFor="continuous">
            {t('form.labels.continuous')}
          </label>
        </div>

        <div className="mb-3">
          <label htmlFor="continuous">{t('voucher.maxUseCount')}</label>
          <input
            type="number"
            name="maxUseCount"
            className="form-control"
            value={(hasUseCountSet ? model?.validity?.maxUseCount : '')}
            onChange={(e) => handleChange(e, 'validity', { maxUseCount: e.target.valueAsNumber })}
            onBlur={(e) => handleDisable(e)}
            disabled={!useCountEnabled}
            min={1}
            step={1}
          />
        </div></> : null}

      {model.useType === 'STAMPS' && !stampRuleCheck ? <>
        <fieldset className="use-type-settings">
          <div className="mb-3">
            <Expression
              expression={stampRuleDefinition.expression || model.rule.definition.expression}
              parameterValues={model.rule?.parameterValues}
              onChange={handleRuleChange} />
          </div>
        </fieldset>
      </> : null}


      <div className="row mb-3">
        <label htmlFor="logo" className="form-label">{t('voucher.validFrom')}</label>
        <Datetime
          utc={true} locale={locale} closeOnSelect={true} isValidDate={cur => cur.isSameOrAfter(moment.utc().subtract(1, 'days'))} inputProps={{ className: 'form-control' }} value={model.validity && model.validity.validSince ? moment.utc(model.validity.validSince).format(dateTimeFormat) : null} onChange={val => handleDateChange('validSince', val)} dateFormat={dateFormat} timeFormat={timeFormat} />
      </div>

      <div className="row mb-3">
        <label htmlFor="logo" className="form-label">{t('voucher.validTo')}</label>
        <Datetime
          utc={true}
          locale={locale}
          closeOnSelect={true}
          isValidDate={cur => !model.validity.validSince || cur.isSameOrAfter(moment(model.validity.validSince))}
          inputProps={{ className: 'form-control' }}
          value={model.validity && model.validity.validUntil ? moment.utc(model.validity.validUntil).format(dateTimeFormat) : null}
          onChange={val => handleDateChange('validUntil', val)} dateFormat={dateFormat} timeFormat={timeFormat}

        />
      </div>

      <div className="row mb-3">
        <div className="col-sm-6 col-xs-12 text-start">
          <LogoInput
            value={model.logo}
            onChange={handleLogoChange}
            validationRules={logoValidationRules}
            translator={(key, params) => t(`logoInput.${key}`, params)} />
        </div>
        <div className="col-sm-3 col-xs-12 text-start" >
          <span>{t('form.labels.logo.preview')}</span>
          { (model.logo || model.logoUrl) && <img
            id="logo"
            alt=""
            className="logo"
            src={model.logo ||model.logoUrl}
            style={{ marginTop: '1rem', width: '250px', height: '175px' }}
          />}
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-sm-6 col-xs-12 text-end">
          {renderIndicatorIfLoading()}
          <button type="submit" className="btn btn-primary" >{t('common:actions:save')}</button>
        </div>

        {children}
      </div>
    </form >
  );
};

const Discount = ({ discountTypes = [], model, onChange, t }) => {

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.name === 'value' ? e.target.valueAsNumber : e.target.value;

    onChange(e, 'discount', { [name]: value });
  };

  return <>
    <div className="col-md-6">

      <label htmlFor="discount-type" className="form-label">{t('voucher.discountType')}</label>
      <select id="discount-type" name="type" className="form-select" onChange={handleChange} value={model.type}>
        {discountTypes.map(({ key, value }) => <option key={key} value={key}>{value}</option>)}
      </select>
    </div>

    <div className="col-md-6">
      <label htmlFor="discount-type" className="form-label">{t('voucher.discountValue')}</label>
      <input type="number" name="value" className="form-control" value={model.value} min={0} max={100} disabled={model.type === 'not-specified'} onChange={handleChange} />
    </div>
  </>;
};

Form.propTypes = {
  model: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    tags: PropTypes.string,
    discount: PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.string
    }),
    validity: PropTypes.shape({
      validSince: PropTypes.string,
      validUntil: PropTypes.string,
      maxUseCount: PropTypes.number,
    }),
  }),
};

Form.defaultProps = {
  model: {},
};

export default Form;
